import { TFunction } from "i18next";

import { ITriggerRawValues } from "../@types/Alerts";

/**
 * The label key for each measurement a trigger can watch, keyed on the measurement field the API
 * stores and sends (`measurementType` in a create request). Shared by the Create trigger "Type"
 * dropdown and the Manage triggers table (EN-5284), so both name a measurement the same way.
 */
export const TRIGGER_TYPE_LABEL_KEYS: Record<string, string> = {
  weight: "patientPage_triggers_type_weight",
  height: "patientPage_triggers_type_height",
  bp_systolic: "patientPage_triggers_type_bpSystolic",
  bp_diastolic: "patientPage_triggers_type_bpDiastolic",
  hemoglobin: "patientPage_triggers_type_hemoglobin",
  cni_trough: "patientPage_triggers_type_tacrolimus",
  blood_glucose: "patientPage_triggers_type_bloodGlucose",
  heart_rate: "patientPage_triggers_type_heartRate",
  pulse_ox: "patientPage_triggers_type_oxygenSaturation",
  temperature: "patientPage_triggers_type_temperature",
  dyspnea: "patientPage_triggers_type_breathlessness",
  fvc: "patientPage_triggers_type_fvc",
  fev1: "patientPage_triggers_type_fev1",
  fev25_75: "patientPage_triggers_type_fef2575",
};

/** The notification channels the API sends, as the Create trigger dropdown labels them. */
const NOTIFICATION_LABEL_KEYS: Record<string, string> = {
  Email: "patientPage_triggers_notify_email",
  SMS: "patientPage_triggers_notify_sms",
  Call: "patientPage_triggers_notify_call",
};

const hasLabel = (keys: Record<string, string>, value: string) =>
  Object.prototype.hasOwnProperty.call(keys, value);

/**
 * A row of the Manage triggers table: the API's formatted English text, plus the values it was
 * formatted from when the API sends them.
 */
type ITriggerRow = ITriggerRawValues & {
  measurementType: string;
  timeFrame: string;
  hcpNotification: string;
};

/**
 * EN-5284: the Manage triggers table's labels, in the user's language.
 *
 * The API formats these in English. Each one is rebuilt from the raw value the API sends next to
 * it, and falls back to the API's text whenever the raw value is missing (an older API) or is one
 * the portal has no label for — so a row never reads worse than it did before.
 */
export function formatTriggerMeasurementType(
  t: TFunction,
  row: Pick<
    ITriggerRow,
    "measurementType" | "measurementField" | "measurementField2"
  >
): string {
  if (!row.measurementField) return row.measurementType;

  const fields = row.measurementField2
    ? [row.measurementField, row.measurementField2]
    : [row.measurementField];

  // The API holds names for more measurements than the dropdown offers; one this list does not
  // know keeps the whole English text rather than mixing languages within a cell.
  if (!fields.every((field) => hasLabel(TRIGGER_TYPE_LABEL_KEYS, field))) {
    return row.measurementType;
  }

  return fields.map((field) => t(TRIGGER_TYPE_LABEL_KEYS[field])).join(", ");
}

/**
 * Mirrors the API's time frame text (AlertsService.GetTimeFrameText): a weight gain trigger reads
 * its own number of days, any other trigger its reading window.
 */
export function formatTriggerTimeFrame(t: TFunction, row: ITriggerRow): string {
  // No comparator means an API that sends no raw values at all.
  if (row.comparator === undefined) return row.timeFrame;

  // The API omits a null, but a null that does arrive means the same "no days".
  const days = row.timeframeDays ?? undefined;

  if (row.comparator === "+") {
    return days === undefined
      ? row.timeFrame
      : t("patientPage_alerts_timeframeDays", { count: days });
  }

  switch (days) {
    case undefined:
      return t("patientPage_alerts_anyReading");
    case 1:
      return t("patientPage_triggers_timeFrame_highestOfDay");
    case 3:
    case 7:
      return t("patientPage_alerts_timeframeDays", { count: days });
    default:
      return row.timeFrame;
  }
}

export function formatTriggerNotifications(
  t: TFunction,
  row: ITriggerRow
): string {
  const types = row.hcpNotificationTypes;

  if (
    !Array.isArray(types) ||
    !types.every((type) => hasLabel(NOTIFICATION_LABEL_KEYS, type))
  ) {
    return row.hcpNotification;
  }

  return types.map((type) => t(NOTIFICATION_LABEL_KEYS[type])).join(", ");
}
